/**
 * GameState - 游戏状态管理类
 * @description 管理关卡进度、解锁状态和最佳成绩，使用LocalStorage持久化
 */

/** 关卡信息 */
export interface LevelInfo {
  /** 关卡编号 (1-20) */
  id: number;
  /** 关卡名称 */
  name: string;
  /** 场景key */
  sceneKey: string;
  /** 是否已解锁 */
  unlocked: boolean;
  /** 最佳用时（秒），null表示未通关 */
  bestTime: number | null;
  /** 是否已通关 */
  completed: boolean;
}

/** 游戏状态数据结构 */
export interface GameSaveData {
  /** 版本号，用于兼容性检查 */
  version: string;
  /** 当前选择的关卡 */
  currentLevel: number;
  /** 关卡信息列表 */
  levels: LevelInfo[];
  /** 最后更新时间 */
  lastUpdated: string;
}

/** LocalStorage 键名 */
const STORAGE_KEY = 'physics-game-save';

/** 默认关卡配置 */
const DEFAULT_LEVELS: LevelInfo[] = [
  { id: 1, name: '速度与位移', sceneKey: 'Level1Scene', unlocked: true, bestTime: null, completed: false },
  { id: 2, name: '加速度探索', sceneKey: 'Level2Scene', unlocked: false, bestTime: null, completed: false },
  { id: 3, name: '抛物线运动', sceneKey: 'Level3Scene', unlocked: false, bestTime: null, completed: false },
  { id: 4, name: '摩擦力探索', sceneKey: 'Level4Scene', unlocked: false, bestTime: null, completed: false },
  { id: 5, name: '综合挑战', sceneKey: 'Level5Scene', unlocked: false, bestTime: null, completed: false },
  { id: 6, name: '静电场', sceneKey: 'Level6Scene', unlocked: false, bestTime: null, completed: false },
  { id: 7, name: '磁场与洛伦兹力', sceneKey: 'Level7Scene', unlocked: false, bestTime: null, completed: false },
  { id: 8, name: '电磁感应', sceneKey: 'Level8Scene', unlocked: false, bestTime: null, completed: false },
  { id: 9, name: '安培定律', sceneKey: 'Level9Scene', unlocked: false, bestTime: null, completed: false },
  { id: 10, name: '电磁综合挑战', sceneKey: 'Level10Scene', unlocked: false, bestTime: null, completed: false },
  { id: 11, name: '光速限制', sceneKey: 'Level11Scene', unlocked: false, bestTime: null, completed: false },
  { id: 12, name: '时间膨胀', sceneKey: 'Level12Scene', unlocked: false, bestTime: null, completed: false },
  { id: 13, name: '长度收缩', sceneKey: 'Level13Scene', unlocked: false, bestTime: null, completed: false },
  { id: 14, name: '红移蓝移', sceneKey: 'Level14Scene', unlocked: false, bestTime: null, completed: false },
  { id: 15, name: '相对论综合挑战', sceneKey: 'Level15Scene', unlocked: false, bestTime: null, completed: false },
  { id: 16, name: '黑洞引力', sceneKey: 'Level16Scene', unlocked: false, bestTime: null, completed: false },
  { id: 17, name: '引力透镜', sceneKey: 'Level17Scene', unlocked: false, bestTime: null, completed: false },
  { id: 18, name: '引力红移', sceneKey: 'Level18Scene', unlocked: false, bestTime: null, completed: false },
  { id: 19, name: '霍金辐射', sceneKey: 'Level19Scene', unlocked: false, bestTime: null, completed: false },
  { id: 20, name: '宇宙创生', sceneKey: 'Level20Scene', unlocked: false, bestTime: null, completed: false },
];

/** 当前版本 */
const CURRENT_VERSION = '1.0.0';

/** 测试用 localStorage 注入（依赖注入模式） */
let injectedStorage: Storage | null = null;

/**
 * GameState 类
 * 单例模式，管理游戏进度
 */
export class GameState {
  private static instance: GameState | null = null;
  private saveData: GameSaveData;
  private listeners: Set<(data: GameSaveData) => void> = new Set();

  /**
   * 注入 localStorage（用于测试）
   */
  static injectStorage(storage: Storage): void {
    injectedStorage = storage;
  }

  /**
   * 获取当前的 localStorage 引用
   */
  private static getStorage(): Storage {
    return injectedStorage ?? localStorage;
  }

  /**
   * 获取单例实例
   */
  static getInstance(): GameState {
    if (!GameState.instance) {
      GameState.instance = new GameState();
    }
    return GameState.instance;
  }

  /**
   * 私有构造函数，防止外部创建实例
   */
  private constructor() {
    this.saveData = this.loadFromStorage();
  }

  /**
   * 从LocalStorage加载存档数据
   */
  private loadFromStorage(): GameSaveData {
    try {
      const storage = GameState.getStorage();
      const stored = storage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored) as GameSaveData;
        // 版本兼容性检查
        if (data.version === CURRENT_VERSION) {
          return data;
        }
      }
    } catch (e) {
      console.warn('GameState: 加载存档失败，使用默认数据', e);
    }
    // 返回默认数据
    return this.createDefaultData();
  }

  /**
   * 创建默认存档数据
   */
  private createDefaultData(): GameSaveData {
    // 深拷贝 DEFAULT_LEVELS，防止修改影响原始数据
    const levels = DEFAULT_LEVELS.map(l => ({ ...l }));
    return {
      version: CURRENT_VERSION,
      currentLevel: 1,
      levels,
      lastUpdated: new Date().toISOString(),
    };
  }

  /**
   * 保存数据到LocalStorage
   */
  private saveToStorage(): void {
    try {
      this.saveData.lastUpdated = new Date().toISOString();
      GameState.getStorage().setItem(STORAGE_KEY, JSON.stringify(this.saveData));
      this.notifyListeners();
    } catch (e) {
      console.warn('GameState: 保存存档失败', e);
    }
  }

  /**
   * 通知所有监听器
   */
  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener(this.saveData);
    }
  }

  /**
   * 添加状态变化监听器
   */
  addListener(listener: (data: GameSaveData) => void): void {
    this.listeners.add(listener);
  }

  /**
   * 移除状态变化监听器
   */
  removeListener(listener: (data: GameSaveData) => void): void {
    this.listeners.delete(listener);
  }

  /**
   * 获取所有关卡信息
   */
  getLevels(): LevelInfo[] {
    return [...this.saveData.levels];
  }

  /**
   * 获取指定关卡信息
   */
  getLevel(levelId: number): LevelInfo | null {
    return this.saveData.levels.find(l => l.id === levelId) ?? null;
  }

  /**
   * 检查关卡是否解锁
   */
  isLevelUnlocked(levelId: number): boolean {
    const level = this.getLevel(levelId);
    return level?.unlocked ?? false;
  }

  /**
   * 检查关卡是否已通关
   */
  isLevelCompleted(levelId: number): boolean {
    const level = this.getLevel(levelId);
    return level?.completed ?? false;
  }

  /**
   * 获取关卡最佳用时
   */
  getBestTime(levelId: number): number | null {
    const level = this.getLevel(levelId);
    return level?.bestTime ?? null;
  }

  /**
   * 获取当前关卡
   */
  getCurrentLevel(): number {
    return this.saveData.currentLevel;
  }

  /**
   * 设置当前关卡
   */
  setCurrentLevel(levelId: number): void {
    if (levelId >= 1 && levelId <= 20 && this.isLevelUnlocked(levelId)) {
      this.saveData.currentLevel = levelId;
      this.saveToStorage();
    }
  }

  /**
   * 通关关卡，解锁下一关
   * @param levelId 关卡编号
   * @param completionTime 完成用时（秒）
   * @returns 是否解锁了新关卡
   */
  completeLevel(levelId: number, completionTime: number): boolean {
    const level = this.getLevel(levelId);
    if (!level || !level.unlocked) return false;

    // 更新关卡状态
    level.completed = true;
    
    // 更新最佳用时
    if (level.bestTime === null || completionTime < level.bestTime) {
      level.bestTime = completionTime;
    }

    // 解锁下一关
    let unlockedNew = false;
    if (levelId < 20) {
      const nextLevel = this.getLevel(levelId + 1);
      if (nextLevel && !nextLevel.unlocked) {
        nextLevel.unlocked = true;
        unlockedNew = true;
      }
    }

    this.saveToStorage();
    return unlockedNew;
  }

  /**
   * 获取已通关关卡数量
   */
  getCompletedCount(): number {
    return this.saveData.levels.filter(l => l.completed).length;
  }

  /**
   * 获取已解锁关卡数量
   */
  getUnlockedCount(): number {
    return this.saveData.levels.filter(l => l.unlocked).length;
  }

  /**
   * 检查是否所有关卡已通关
   */
  isAllCompleted(): boolean {
    return this.saveData.levels.every(l => l.completed);
  }

  /**
   * 重置所有进度（清除存档）
   */
  resetProgress(): void {
    this.saveData = this.createDefaultData();
    this.saveToStorage();
  }

  /**
   * 获取完整存档数据（用于调试/测试）
   */
  getSaveData(): GameSaveData {
    return { ...this.saveData };
  }

  /**
   * 强制解锁指定关卡（用于测试）
   */
  forceUnlock(levelId: number): void {
    const level = this.getLevel(levelId);
    if (level) {
      level.unlocked = true;
      this.saveToStorage();
    }
  }

  /**
   * 清除单例实例（用于测试）
   */
  static resetInstance(): void {
    GameState.instance = null;
  }
}

// 导出单例获取函数，便于使用
export const getGameState = (): GameState => GameState.getInstance();